import _ from 'lodash';
import _isEmpty from 'lodash/isEmpty';
import React, { Component } from 'react';
import { reverse } from 'named-urls';
import routes from '@achievable-me/tundra-wizard/routes';
import USER_EXAM_STATUS from '@achievable-me/tundra-wizard/userExamStatus';
import { countItemsAvailable } from '@achievable-me/tundra-wizard/contentHelpers';
import Button from '$components/library/Button';
import STEP from '$components/review/step';
import MODE from '$components/review/mode';
import logger from '$lib/logger';
import styles from './styles.scss';

const SPACER = <div className={styles.spacer} />;

const textbookButton = ({ quizState }) => {
  const { product } = quizState;
  if (!product) {
    return null;
  }

  const { slug } = product;
  const {
    item: { contentUuid, hasTextbookContent },
  } = quizState.quizQueue[0];

  if (!hasTextbookContent || !contentUuid) {
    return null;
  }

  return (
    <div className={styles.spacerStart}>
      <a
        href={reverse(routes.study.learn, { productSlug: slug, contentUuid })}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button className={styles.button} key="textbook" data-aaa="actionTextbookButton">
          Textbook <i className="fad fa-external-link" />
        </Button>
      </a>
    </div>
  );
};

class ReviewActions extends Component {
  renderEssayActions = () => {
    const {
      handleEssayStart,
      handleEssaySubmit,
      handleEssayViewEssay,
      handleEssayViewResults,
      quizState,
    } = this.props;
    const { step, essayResponse } = quizState;
    const submitAt = _.get(essayResponse, 'submitAt');

    switch (step) {
      case STEP.loading: {
        return null;
      }
      case STEP.essayInstructions: {
        return (
          <div className={styles.spacerCenter}>
            <Button
              color="teal"
              className={styles.button}
              key="start"
              data-aaa="actionEssayStart"
              onClick={handleEssayStart}
            >
              Start writing
            </Button>
          </div>
        );
      }
      case STEP.essayWriting: {
        if (submitAt) {
          return (
            <div className={styles.spacerCenter}>
              <Button
                className={styles.button}
                key="viewFeedback"
                data-aaa="actionEssayViewFeedback"
                onClick={handleEssayViewResults}
              >
                View feedback
              </Button>
            </div>
          );
        }

        return (
          <div className={styles.spacerEnd}>
            <Button
              color="teal"
              className={styles.button}
              key="submit"
              data-aaa="actionEssaySubmit"
              onClick={handleEssaySubmit}
            >
              Submit
            </Button>
          </div>
        );
      }
      case STEP.essayReview: {
        return (
          <div className={styles.spacerCenter}>
            <Button
              className={styles.button}
              key="viewEssay"
              data-aaa="actionEssayViewEssay"
              onClick={handleEssayViewEssay}
            >
              View essay
            </Button>
          </div>
        );
      }
      default: {
        logger.warn(`Unknown step: ${step}`);
        return null;
      }
    }
  };

  renderExamActions = () => {
    const {
      handleExamNext,
      handleExamPrev,
      handleExamReview,
      handleExamSummary,
      handleExit,
      handleMarkExamItem,
      handleShowQuizConfirmation,
      handleShowQuizExplanation,
      handleToggleShowPassage,
      quizState,
    } = this.props;
    const { step, showPassage } = quizState;

    if (showPassage) {
      return (
        <div className={styles.spacerStart}>
          <Button
            className={styles.button}
            key="passage"
            data-aaa="actionPassageButton"
            onClick={handleToggleShowPassage}
          >
            Back to quiz
          </Button>
        </div>
      );
    }

    switch (step) {
      case STEP.loading: {
        return null;
      }
      case STEP.examNavigation: {
        const { userExam } = quizState;
        const isFinished = userExam.status === USER_EXAM_STATUS.finished;

        const ctaButton = isFinished ? (
          <Button
            className={styles.button}
            key="summary"
            data-aaa="actionExamSummaryButton"
            color="teal"
            onClick={handleExamSummary}
          >
            Summary
          </Button>
        ) : (
          <Button
            className={styles.button}
            key="review"
            data-aaa="actionExamReviewButton"
            color="teal"
            onClick={handleExamReview}
          >
            Review
          </Button>
        );

        return (
          <div className={styles.contentsSpread}>
            <Button
              className={styles.button}
              key="exit"
              data-aaa="actionExamExitButton"
              onClick={handleExit}
            >
              Exit
            </Button>
            {ctaButton}
          </div>
        );
      }
      case STEP.examReview: {
        return (
          <div className={styles.contentsStart}>
            <Button
              className={styles.button}
              key="prev"
              data-aaa="actionExamPrevButton"
              onClick={handleExamPrev}
            >
              Prev
            </Button>
            <div className={styles.spacer} />
          </div>
        );
      }
      case STEP.examSummary: {
        const { userExam } = quizState;
        const cta = userExam.status === USER_EXAM_STATUS.finished ? 'Review' : 'Start';

        return (
          <div className={styles.contentsSpread}>
            <Button
              className={styles.button}
              key="exit"
              data-aaa="actionExamExitButton"
              onClick={handleExit}
            >
              Exit
            </Button>
            <Button
              className={styles.button}
              key="next"
              data-aaa="actionExamNextButton"
              color="teal"
              onClick={handleExamNext}
            >
              {cta}
            </Button>
          </div>
        );
      }
      case STEP.quizPrompt: {
        const { examQueueIndex, quizQueue } = quizState;
        const shouldShowPrev = examQueueIndex > 0;
        const shouldShowNext = examQueueIndex < quizQueue.length - 1;
        const shouldShowFlag = step === STEP.quizPrompt;
        const { isFlagged } = quizQueue[examQueueIndex].item;

        const flagAction = (() => {
          if (!shouldShowFlag) {
            return null;
          }

          return (
            <div className={styles.spacerCenter}>
              <Button
                className={styles.button}
                key="flag"
                data-aaa="actionExamFlagButton"
                onClick={() => handleMarkExamItem({ isFlagged: !isFlagged })}
              >
                {isFlagged ? 'Unflag' : 'Flag'}
              </Button>
            </div>
          );
        })();

        const nextAction = (() => {
          if (shouldShowNext) {
            return (
              <div className={styles.spacerEnd}>
                <Button
                  className={styles.button}
                  key="next"
                  data-aaa="actionExamNextButton"
                  color="teal"
                  onClick={handleExamNext}
                >
                  Next
                </Button>
              </div>
            );
          }

          return (
            <div className={styles.spacerEnd}>
              <Button
                className={styles.button}
                key="review"
                data-aaa="actionExamReviewButton"
                color="teal"
                onClick={handleExamReview}
              >
                Next
              </Button>
            </div>
          );
        })();

        return (
          <div className={styles.contentsSpread}>
            {shouldShowPrev ? (
              <div className={styles.spacerStart}>
                <Button
                  className={styles.button}
                  key="prev"
                  data-aaa="actionExamPrevButton"
                  onClick={handleExamPrev}
                >
                  Prev
                </Button>
              </div>
            ) : (
              SPACER
            )}
            {flagAction}
            {nextAction}
          </div>
        );
      }
      case STEP.quizConfirmation:
      case STEP.quizExplanation: {
        const { examQueueIndex, quizQueue } = quizState;
        const shouldShowPrev = examQueueIndex > 0;
        const shouldShowNext = examQueueIndex < quizQueue.length - 1;

        const flipAction = (() => {
          const buttonComponent =
            step === STEP.quizConfirmation ? (
              <Button
                className={styles.button}
                key="flip"
                data-aaa="actionExamFlipButton"
                onClick={handleShowQuizExplanation}
              >
                Explanation
              </Button>
            ) : (
              <Button
                className={styles.button}
                key="flip"
                data-aaa="actionExamFlipButton"
                onClick={handleShowQuizConfirmation}
              >
                Quiz
              </Button>
            );

          return <div className={styles.spacerCenter}>{buttonComponent}</div>;
        })();

        const nextAction = (() => {
          if (shouldShowNext) {
            return (
              <div className={styles.spacerEnd}>
                <Button
                  className={styles.button}
                  key="next"
                  data-aaa="actionExamNextButton"
                  color="teal"
                  onClick={handleExamNext}
                >
                  Next
                </Button>
              </div>
            );
          }

          return (
            <div className={styles.spacerEnd}>
              <Button
                className={styles.button}
                key="summary"
                data-aaa="actionExamSummaryButton"
                color="teal"
                onClick={handleExamSummary}
              >
                Summary
              </Button>
            </div>
          );
        })();

        return (
          <div className={styles.contentsSpread}>
            {shouldShowPrev ? (
              <div className={styles.spacerStart}>
                <Button
                  className={styles.button}
                  key="prev"
                  data-aaa="actionExamPrevButton"
                  onClick={handleExamPrev}
                >
                  Prev
                </Button>
              </div>
            ) : (
              SPACER
            )}
            {flipAction}
            {nextAction}
          </div>
        );
      }
      default: {
        logger.warn(`Unknown step: ${step}`);
        return null;
      }
    }
  };

  renderReviewActions = () => {
    const {
      handleConfirm,
      handleContinue,
      handleDebugReset,
      handleExit,
      handleMore,
      handleOptionsApply,
      handleOptionsReset,
      handleShowQuizConfirmation,
      handleShowQuizExplanation,
      handleStart,
      handleToggleShowPassage,
      quizState,
      mode,
    } = this.props;
    const { step, showPassage } = quizState;

    if (showPassage) {
      return (
        <div className={styles.spacerStart}>
          <Button
            className={styles.button}
            key="passage"
            data-aaa="actionPassageButton"
            onClick={handleToggleShowPassage}
          >
            Back to quiz
          </Button>
        </div>
      );
    }

    switch (step) {
      case STEP.sessionOptions: {
        const { content, options } = quizState;
        const { contentUuids } = options;
        const itemsSelected = countItemsAvailable({ contentUuids, content });

        return (
          <div className={styles.contentsSpread}>
            <div className={styles.spacerStart}>
              <Button
                className={styles.button}
                key="optionsReset"
                data-aaa="actionOptionsReset"
                onClick={handleOptionsReset}
              >
                Reset
              </Button>
            </div>
            <div className={styles.spacerEnd}>
              <Button
                className={styles.button}
                key="optionsApply"
                data-aaa="actionOptionsApply"
                color="teal"
                onClick={handleOptionsApply}
                disabled={_isEmpty(contentUuids) || !itemsSelected}
              >
                Apply
              </Button>
            </div>
          </div>
        );
      }
      case STEP.sessionSelect: {
        return (
          <div className={styles.contentsEnd}>
            <Button
              className={styles.button}
              key="start"
              data-aaa="actionStartButton"
              color="green"
              onClick={handleStart}
            >
              Start
            </Button>
          </div>
        );
      }
      case STEP.loading: {
        return null;
      }
      case STEP.quizPrompt: {
        const { quizQueue } = quizState;
        const [
          {
            quiz: { input, evaluated },
            item: { itemType },
          },
        ] = quizQueue;
        const { choices } = evaluated;

        const canConfirm =
          (itemType === 'multipleChoice' && input.s) ||
          (itemType === 'numeric' && input.s) ||
          (itemType === 'passageChooseOne' && input.s) ||
          (itemType === 'passageChooseMany' && true) ||
          (itemType === 'passageChooseSentence' && input.s) ||
          (itemType === 'quantityComparison' && input.s) ||
          (itemType === 'selectMultiple' && true) ||
          (itemType === 'sentenceEquivalence' && input.s && input.s.length === 2) ||
          (itemType === 'clozeDeletion' &&
            input.s &&
            input.s.length === choices.length) ||
          (itemType === 'actEnglishMultipleChoice' && input.s);

        return (
          <div className={styles.spacerCenter}>
            <Button
              className={styles.button}
              disabled={!canConfirm}
              key="confirm"
              data-aaa="actionConfirmButton"
              color="teal"
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </div>
        );
      }
      case STEP.quizConfirmation: {
        const { quizQueue } = quizState;
        const [
          {
            quiz: { evaluated },
          },
        ] = quizQueue;
        const { explanation } = evaluated;
        const hasExplanation = !!explanation;

        if (!hasExplanation) {
          return (
            <div className={styles.contentsSpread}>
              {textbookButton({ quizState }) || SPACER}
              <div className={styles.spacerEnd}>
                <Button
                  className={styles.button}
                  key="continue"
                  data-aaa="actionContinueButton"
                  color="teal"
                  onClick={handleContinue}
                >
                  Next
                </Button>
              </div>
            </div>
          );
        }

        return (
          <div className={styles.contentsSpread}>
            {SPACER}
            <div className={styles.spacerCenter}>
              <Button
                className={styles.button}
                key="flip"
                data-aaa="actionFlipButton"
                onClick={handleShowQuizExplanation}
              >
                Explanation
              </Button>
            </div>
            <div className={styles.spacerEnd}>
              <Button
                className={styles.button}
                key="continue"
                data-aaa="actionContinueButton"
                color="teal"
                onClick={handleContinue}
              >
                Next
              </Button>
            </div>
          </div>
        );
      }
      case STEP.quizExplanation: {
        return (
          <div className={styles.contentsSpread}>
            {textbookButton({ quizState }) || SPACER}
            <div className={styles.spacerCenter}>
              <Button
                className={styles.button}
                key="flip"
                data-aaa="actionFlipButton"
                onClick={handleShowQuizConfirmation}
              >
                Quiz
              </Button>
            </div>
            <div className={styles.spacerEnd}>
              <Button
                className={styles.button}
                key="continue"
                data-aaa="actionContinueButton"
                color="teal"
                onClick={handleContinue}
              >
                Next
              </Button>
            </div>
          </div>
        );
      }
      case STEP.summary: {
        if (mode === MODE.share) {
          return null;
        }

        if (mode === MODE.debug) {
          return (
            <div className={styles.contentsEnd}>
              <Button
                className={styles.button}
                key="exit"
                data-aaa="actionExitButton"
                color="green"
                onClick={handleDebugReset}
              >
                Reset
              </Button>
            </div>
          );
        }

        return (
          <div className={styles.contentsEnd}>
            <Button
              className={styles.button}
              key="exit"
              data-aaa="actionExitButton"
              color="green"
              onClick={handleExit}
            >
              Exit
            </Button>
            <Button
              className={styles.button}
              key="more"
              data-aaa="actionMoreButton"
              color="teal"
              onClick={handleMore}
            >
              More questions
            </Button>
          </div>
        );
      }
      default: {
        logger.warn(`Unknown step: ${step}`);
        return null;
      }
    }
  };

  renderActions = () => {
    const { mode } = this.props;

    const ACTIONS_MAP = {
      [MODE.exam]: this.renderExamActions,
      [MODE.essay]: this.renderEssayActions,
    };

    const actionsFn = ACTIONS_MAP[mode] || this.renderReviewActions;
    return actionsFn();
  };

  render() {
    return <div className={styles.layout}>{this.renderActions()}</div>;
  }
}

export default ReviewActions;
